import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Card, CardBody, Chip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { ErrorIllustration } from './illustrations/ErrorIllustration';
import { OfflineIllustration } from './illustrations/OfflineIllustration';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorType: 'network' | 'module' | 'general';
}

interface ErrorBoundaryUIProps extends Props {
  t: (key: string) => string;
}

class ErrorBoundaryCore extends Component<ErrorBoundaryUIProps, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorType: 'general',
  };

  public static getDerivedStateFromError(error: Error): State {
    const errorMessage = error.message || error.toString();
    let errorType: 'network' | 'module' | 'general' = 'general';

    if (
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('Failed to load') ||
      errorMessage.includes('dynamically imported module')
    ) {
      errorType = errorMessage.includes('dynamically imported module') ? 'module' : 'network';
    }

    return { hasError: true, error, errorType };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🚨 ErrorBoundary caught an error:', error);
    console.error('📋 Error Info:', errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorType: 'general' });
    window.location.reload();
  };

  private getErrorContent() {
    const { errorType } = this.state;
    const { t } = this.props;

    switch (errorType) {
      case 'module':
      case 'network':
        return {
          title: t('connectionFailed'),
          message: t('checkInternetConnection'),
          illustration: <OfflineIllustration width={280} height={220} />,
          chipColor: 'warning' as const,
          chipText: t('networkErrorChip'),
          actionText: t('tryAgain'),
        };
      default:
        return {
          title: t('somethingWentWrong'),
          message: t('unexpectedError'),
          illustration: <ErrorIllustration width={280} height={220} />,
          chipColor: 'danger' as const,
          chipText: t('systemError'),
          actionText: t('reloadPage'),
        };
    }
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const content = this.getErrorContent();
      const { t } = this.props;

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background to-default-100">
          <Card className="max-w-lg w-full shadow-2xl border border-default-200">
            <CardBody className="p-10">
              <div className="flex flex-col items-center gap-6 text-center">
                <div className="w-full flex justify-center">
                  {content.illustration}
                </div>

                <Chip
                  color={content.chipColor}
                  variant="flat"
                  size="sm"
                  className="font-medium"
                >
                  {content.chipText}
                </Chip>

                <div className="space-y-3">
                  <h2 className="text-2xl font-bold text-default-900">
                    {content.title}
                  </h2>
                  <p className="text-base text-default-700">
                    {content.message}
                  </p>
                </div>

                {this.state.error && (
                  <details className="w-full text-left">
                    <summary className="text-xs text-default-400 cursor-pointer hover:text-default-600 transition-colors flex items-center gap-2">
                      <Icon icon="lucide:info" width={14} />
                      <span>{t('technicalDetails')}</span>
                    </summary>
                    <pre className="mt-3 p-4 bg-default-100 dark:bg-default-50 rounded-lg text-xs overflow-auto max-h-40 border border-default-200 text-default-700 font-mono">
                      {this.state.error.toString()}
                    </pre>
                  </details>
                )}

                <div className="flex gap-3 w-full pt-2">
                  <Button
                    color={content.chipColor}
                    variant="shadow"
                    startContent={<Icon icon="lucide:refresh-cw" width={18} />}
                    onClick={this.handleReset}
                    className="flex-1 font-medium"
                    size="lg"
                  >
                    {content.actionText}
                  </Button>
                  <Button
                    variant="bordered"
                    startContent={<Icon icon="lucide:home" width={18} />}
                    onClick={() => (window.location.href = '/')}
                    className="font-medium"
                    size="lg"
                  >
                    {t('home')}
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundary: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  return <ErrorBoundaryCore {...props} t={t} />;
};

export default ErrorBoundary;
